
import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    device: '',
    issue: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d68p7hua728k8ctu2d0g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          device: '',
          issue: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-16">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              수리 상담 문의
            </h2>
            <p className="text-gray-600 text-base mb-12 leading-relaxed">
              기기 수리에 대해 궁금하신 점이 있으시면 언제든지 문의해주세요. 빠르게 답변드리겠습니다.
            </p>

            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#007AFF]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-[#007AFF] text-xl"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">주소</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    청주시 상당구 성언로 19-5<br />
                    (성안길 다이소 입구 앞)
                  </p>
                  <a
                    href="https://maps.google.com/?q=청주시+상당구+성언로+19-5"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[#007AFF] text-sm mt-2 hover:underline cursor-pointer whitespace-nowrap"
                  >
                    지도에서 보기
                    <i className="ri-external-link-line"></i>
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#007AFF]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-[#007AFF] text-xl"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">전화</h3>
                  <a
                    href="tel:010-4687-3384"
                    className="text-gray-600 text-sm hover:text-[#007AFF] cursor-pointer whitespace-nowrap"
                  >
                    010-4687-3384
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#007AFF]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-chat-3-line text-[#007AFF] text-xl"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">블로그</h3>
                  <a
                    href="https://blog.naver.com/roy1967"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 text-sm hover:text-[#007AFF] cursor-pointer whitespace-nowrap"
                  >
                    blog.naver.com/roy1967
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#007AFF]/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-text-line text-[#007AFF] text-xl"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">사업자 정보</h3>
                  <p className="text-gray-600 text-sm">사업자번호: 223-07-51528</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-3xl p-8 md:p-12">
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  이름 *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007AFF] focus:border-transparent outline-none transition-all text-sm"
                  placeholder="홍길동"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  연락처 *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007AFF] focus:border-transparent outline-none transition-all text-sm"
                  placeholder="010-1234-5678"
                />
              </div>

              <div>
                <label htmlFor="device" className="block text-sm font-medium text-gray-700 mb-2">
                  기기 모델 *
                </label>
                <input
                  type="text"
                  id="device"
                  name="device"
                  required
                  value={formData.device}
                  onChange={(e) => setFormData({ ...formData, device: e.target.value })}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007AFF] focus:border-transparent outline-none transition-all text-sm"
                  placeholder="예: iPhone 14 Pro"
                />
              </div>

              <div>
                <label htmlFor="issue" className="block text-sm font-medium text-gray-700 mb-2">
                  수리 항목 *
                </label>
                <select
                  id="issue"
                  name="issue"
                  required
                  value={formData.issue}
                  onChange={(e) => setFormData({ ...formData, issue: e.target.value })}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007AFF] focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                >
                  <option value="">선택해주세요</option>
                  <option value="화면 교체">화면 교체</option>
                  <option value="배터리 교체">배터리 교체</option>
                  <option value="카메라 수리">카메라 수리</option>
                  <option value="충전 불량">충전 불량</option>
                  <option value="수리 불가">수리 불가</option>
                  <option value="기타">기타</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  상세 내용
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007AFF] focus:border-transparent outline-none transition-all resize-none text-sm"
                  placeholder="증상을 자세히 설명해주세요 (최대 500자)"
                />
                <div className="text-right text-xs text-gray-500 mt-1">
                  {formData.message.length}/500
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#007AFF] text-white py-4 rounded-full font-medium hover:bg-[#0051D5] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap text-base"
              >
                {isSubmitting ? '전송 중...' : '상담 신청하기'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                  문의가 성공적으로 전송되었습니다. 빠른 시일 내에 연락드리겠습니다.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                  전송 중 오류가 발생했습니다. 다시 시도해주세요.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
